<template>
  <div class="main pb-5">
    <div class="title mb-3">{{ $t('teamScoreViewer.title') }}</div>
    <b-container fluid class="filter-section">
      <b-row class="text-center">
        <b-col cols="4" class="filter-label">{{ $t('filters.phase') }}</b-col>
        <b-col cols="4" class="filter-label">{{ $t('filters.serie') }}</b-col>
        <b-col cols="4" class="filter-label">{{ $t('filters.group') }}</b-col>
      </b-row>
      <b-row class="text-center">
        <b-col cols="4">
          <combo-or-label v-model="selectedPhase" :options="availablePhases"></combo-or-label>
        </b-col>
        <b-col cols="4">
          <combo-or-label v-model="selectedSerie" :options="availableSeries"></combo-or-label>
        </b-col>
        <b-col cols="4">
          <combo-or-label v-model="selectedSector" :options="availableSectors"></combo-or-label>
        </b-col>
      </b-row>
      <b-row class="text-center mt-2">
        <b-col cols="4" class="filter-label">{{ $t('filters.session') }}</b-col>
        <b-col cols="4" class="filter-label">{{ $t('filters.round') }}</b-col>
        <b-col cols="4" class="filter-label">{{ $t('filters.table') }}</b-col>
      </b-row>
      <b-row class="text-center">
        <b-col cols="4">
          <combo-or-label v-model="selectedSession" :options="availableSessions"></combo-or-label>
        </b-col>
        <b-col cols="4">
          <combo-or-label v-model="selectedRound" :options="availableRounds"></combo-or-label>
        </b-col>
        <b-col cols="4">
          <combo-or-label v-model="selectedTable" :options="availableTables"></combo-or-label>
        </b-col>
      </b-row>
    </b-container>
    <b-table :fields="fieldsToBeShown" :items="scores" small striped class="score-table">
      <template #thead-top="">
        <b-tr v-if="currentMatch != null">
          <b-th :colspan="totalFieldCount" class="p-1">
            <div class="teams-header">
              <div class="team-row">
                <span class="homeColor team-name">{{ currentMatch.TeamHName }}</span>
                <span v-if="showImps" class="homeColor imps-total">
                  {{ scores.map(sc => sc.Score.HomeTeamImps).reduce((a, b) => a + b, 0) }}
                </span>
              </div>
              <div class="team-row">
                <span class="visitColor team-name">{{ currentMatch.TeamVName }}</span>
                <span v-if="showImps" class="visitColor imps-total">
                  {{ scores.map(sc => sc.Score.VisitorTeamImps).reduce((a, b) => a + b, 0) }}
                </span>
              </div>
            </div>
          </b-th>
        </b-tr>
      </template>
      <template #cell(ac)>
        {{ $t('teamScoreViewer.openRoomAbbr') }}<br>
        {{ $t('teamScoreViewer.closedRoomAbbr') }}
      </template>
      <template #cell(contracts)="data">
        <div v-html="addColorToSuit(data.item.Score.OpenContract) + '&nbsp;' + data.item.Score.OpenDich"></div>
        <div v-html="addColorToSuit(data.item.Score.ClosedContract) + '&nbsp;' + data.item.Score.ClosedDich"></div>
      </template>
      <template #cell(lead)="data">
        <div v-html="addColorToSuit(data.item.Score.RawOpenAtt) || '&nbsp;'"></div>
        <div v-html="addColorToSuit(data.item.Score.RawClosedAtt) || '&nbsp;'"></div>
      </template>
      <template #cell(ptScore)="data">
        <div v-html="data.item.Score.OpenRoomScore || '&nbsp;'"></div>
        <div v-html="data.item.Score.ClosedRoomScore || '&nbsp;'"></div>
      </template>
      <template #cell(himps)="data">
        <div class="homeColor">{{ data.item.Score.HomeTeamImps || '&nbsp;' }}</div>
      </template>
      <template #cell(vimps)="data">
        <div class="visitColor">{{ data.item.Score.VisitorTeamImps || '&nbsp;' }}</div>
      </template>
      <template #cell(scoDiff)="data">
        {{
          data.item.Score.OpenRoomScore === "" || data.item.Score.ClosedRoomScore === "" ? '&nbsp;' : (data.item.Score.OpenRoomScore - data.item.Score.ClosedRoomScore)
        }}
      </template>
      <template #cell(show_details)="row">
        <b-icon-search
            v-if="!row.detailsShowing && row.item.Deal != null && row.item.Deal.N !== null && (row.item.Score.OpenRoomScore !== '' || row.item.Score.ClosedRoomScore !== '')"
            @click="openDetails(row.item.key)"></b-icon-search>
      </template>

      <template #row-details="row">
        <deal :deal="row.item.Deal" :deal-number="row.item.BoardNumber" class="mt-3" ref="deal"></deal>
        <b-button class="mt-4 float-right" variant="primary" @click="closeDetails">{{ $t('ui.close') }}</b-button>
      </template>
    </b-table>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import ComboOrLabel from "../components/lib/ComboOrLabel";
import Deal from "../components/display/Deal";
import {addColorToSuite as _addColorToSuite} from "../lib/cardSuits";

export default {
  name: 'TeamScoreViewer',
  components: {Deal, ComboOrLabel},
  data() {
    return {
      selectedPhase: 1,
      selectedSerie: 1,
      selectedSector: 1,
      selectedSession: 1,
      selectedRound: 1,
      selectedTable: 1,
      currentDetailsId: -1
    };
  },
  computed: {
    ...mapGetters({
      players: 'players/playersList',
      played: 'deals/played',
      sectors: 'tournament/sector',
      matches: 'matches/matches'
    }),
    scores() {
      const that = this;
      const baseKey = this.selectedSerie + "_"
          + this.selectedSerie + "_"
          + this.selectedSector + "_"
          + this.selectedSession + "_"
          + this.selectedRound + "_"
          + this.selectedTable;

      const played = this.played
          ?.Phases[this.selectedPhase]
          ?.Series[this.selectedSerie]
          ?.Groups[this.selectedSector]
          ?.Sessions[this.selectedSession]
          ?.Rounds[this.selectedRound]
          ?.Deals;

      let currentRoundReducedMap =
          Object.entries(
              this.played
                  ?.Phases[this.selectedPhase]
                  ?.Series[this.selectedSerie]
                  ?.Groups[this.selectedSector]
                  ?.Sessions[this.selectedSession]
                  ?.Rounds[this.selectedRound]
                  ?.Tables[this.selectedTable]
                  ?.Scores ?? {}
          ).map(row => {
            let boardNumber;
            let score;
            [boardNumber, score] = row;
            const ret = {...score, BoardNumber: boardNumber, Deal: played[boardNumber]};

            ret.key = baseKey + "_" + boardNumber;
            ret._showDetails = ret.key === that.currentDetailsId;
            return ret;
          });
      return currentRoundReducedMap != null ? currentRoundReducedMap.sort((a, b) => a.Number - b.Number) : [];
    },
    currentMatch() {
      return this.matches.find(match =>
          // eslint-disable-next-line eqeqeq
          match.Series == this.selectedSerie &&
          match.Group == this.selectedSector &&
          match.Session == this.selectedSession &&
          match.Round == this.selectedRound &&
          match.Tavolo == this.selectedTable
      );
    },
    availablePhases() {
      return Object.keys(this.played?.Phases ?? {}).sort((a, b) => a - b);
    },
    availableSeries() {
      return Object.keys(
          this.played.Phases[this.selectedPhase]?.Series ?? {}
      ).map(seriesId => {
        return {
          value: seriesId,
          text: this.sectors?.Phases?.[this.selectedPhase]?.Series?.[seriesId]?.Sigla ?? String(seriesId)
        }
      }).sort((a, b) => a.text.localeCompare(b.text));
    },
    availableSectors() {
      return Object.keys(
          this.played
              ?.Phases[this.selectedPhase]
              ?.Series[this.selectedSerie]
              ?.Groups ?? {}
      ).map(groupId => {
        return {
          value: groupId,
          text: this.groupSigla(groupId) ?? String(groupId)
        }
      }).sort((a, b) => a.text.localeCompare(b.text));
    },
    availableSessions() {
      if (this.selectedSector === -1) {
        return [];
      }
      return Object.keys(
          this.played
              ?.Phases[this.selectedPhase]
              ?.Series[this.selectedSerie]
              ?.Groups[this.selectedSector]
              ?.Sessions ?? {}
      ).sort((a, b) => a - b);
    },
    availableRounds() {
      if (this.selectedSession === -1) {
        return [];
      }
      return Object.keys(
          this.played
              ?.Phases[this.selectedPhase]
              ?.Series[this.selectedSerie]
              ?.Groups[this.selectedSector]
              ?.Sessions[this.selectedSession]
              ?.Rounds ?? {}
      ).sort((a, b) => a - b);
    },
    availableTables() {
      if (this.selectedRound === -1) {
        return [];
      }
      return Object.keys(
          this.played
              ?.Phases[this.selectedPhase]
              ?.Series[this.selectedSerie]
              ?.Groups[this.selectedSector]
              ?.Sessions[this.selectedSession]
              ?.Rounds[this.selectedRound]
              ?.Tables ?? {}
      ).sort((a, b) => a - b);
    },


    showImps() {
      return this.scores.length > 0 && this.scores.every(scr => scr.Score.OpenRoomScore !== "" && scr.Score.ClosedRoomScore !== "");
    },
    totalFieldCount() {
      return this.showImps ? 9 : 6;
    },
    fieldsToBeShown() {
      return [
        {
          key: 'BoardNumber',
          label: '#'
        },
        {
          key: 'ac',
          label: ''
        },
        {
          key: 'contracts',
          label: ''
        },
        {
          key: 'lead',
          label: ''
        },
        {
          key: 'ptScore',
          label: '',
          tdClass: 'text-right'
        },
        ...(this.showImps ? [
          {
            key: 'scoDiff',
            label: this.$t('teamScoreViewer.diffLabel'),
            tdClass: 'text-right',
            thClass: 'text-center'
          },
          {
            key: 'himps',
            label: 'H'
          },
          {
            key: 'vimps',
            label: 'V'
          }] : []),
        {
          key: 'show_details',
          label: ''
        }
      ];
    }
  },
  methods: {
    openDetails(number) {
      this.currentDetailsId = number;
    },
    closeDetails() {
      this.currentDetailsId = "";
    },
    addColorToSuit(contract) {
      return _addColorToSuite(contract);
    },
    //La sigla del girone è definita per sessione: se la sessione selezionata non è
    //(ancora) valida per la serie corrente si ripiega sulla prima disponibile.
    groupSigla(groupId) {
      const sessions = this.sectors
          ?.Phases?.[this.selectedPhase]
          ?.Series?.[this.selectedSerie]
          ?.Sessions ?? {};
      return (sessions[this.selectedSession] ?? Object.values(sessions)[0])
          ?.Groups?.[groupId]?.Sigla;
    },
    //Riallinea la selezione quando le opzioni disponibili cambiano (es. cambio fase
    //con serie non presenti nella nuova fase): senza questo restano valori orfani.
    alignSelection(field, options, preferLast) {
      const values = options.map(option => option.value ?? option);
      // eslint-disable-next-line eqeqeq
      if (values.length === 0 || values.some(value => value == this[field])) {
        return;
      }
      this[field] = preferLast ? values[values.length - 1] : values[0];
    }
  },
  watch: {
    availableSeries: {
      handler(options) {
        this.alignSelection('selectedSerie', options, false);
      },
      immediate: true
    },
    availableSectors: {
      handler(options) {
        this.alignSelection('selectedSector', options, false);
      },
      immediate: true
    },
    availableSessions: {
      handler(options) {
        this.alignSelection('selectedSession', options, true);
      },
      immediate: true
    },
    availableRounds: {
      handler(options) {
        this.alignSelection('selectedRound', options, true);
      },
      immediate: true
    },
    availableTables: {
      handler(options) {
        this.alignSelection('selectedTable', options, false);
      },
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.availablePhases.length > 0) {
        this.selectedPhase = Math.max(...this.availablePhases);
      }
      //I watcher riallineano serie/girone; poi ci si posiziona sull'ultima sessione/turno.
      this.$nextTick(() => {
        if (this.availableSessions.length > 0) {
          this.selectedSession = Math.max(...this.availableSessions);
        }
        this.$nextTick(() => {
          if (this.availableRounds.length > 0) {
            this.selectedRound = Math.max(...this.availableRounds);
          }
        });
      });
    });
  }
};
</script>

<style scoped>

table tr td {
  border: 1px solid black;
}

/deep/ .table > tbody > tr > td {
  vertical-align: middle;
  padding: 0.2rem 0.3rem;
  font-size: 0.82rem;
}

/deep/ ._htmlSuiteClass {
  font-size: 1rem;
}

.score-table {
  font-size: 0.82rem;
}

.teams-header {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.team-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.team-name {
  font-size: 0.9rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.imps-total {
  font-size: 0.9rem;
  font-weight: 700;
  white-space: nowrap;
}

/deep/ .homeColor {
  font-weight: bold;
  color: #1d4ed8;
}

/deep/ .visitColor {
  font-weight: bold;
  color: #dc2626;
}

.title {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e3a5f;
  letter-spacing: -0.01em;
}


</style>
